import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Canvas.css';
import Ribbon from './Ribbon';
import { GridArea } from './GridArea.js';
import RightPanel from './RightPanel';
import VectorizeModal from './components/VectorizeModal';
import { GraphicPrimitive } from './components/shapes/GraphicPrimitive';
import CrossAreaParamsDialog from './components/CrossAreaParamsDialog';
import CrossConversionModal from './components/CrossConversionModal';
import { CrossArea } from './components/shapes/CrossArea';
import { useViewport } from './hooks/useViewport';
import { useMirroring } from './hooks/useMirroring';
import { useProjectIO } from './hooks/useProjectIO';
import { useSimulation } from './hooks/useSimulation';
import StatusBar from './StatusBar';


function WorkArea({
  color, setColor,
  fillColor, setFillColor,
  fillProps, setFillProps,
  currentStitchIndex, setCurrentStitchIndex,
  totalStitches, setTotalStitches,
  isPlaying, setIsPlaying,
  simulationSpeed, setSimulationSpeed
}) {
  const canvasRef = useRef(null);
  const gridAreaRef = useRef(null);
  const containerRef = useRef(null); // Ref for the resize container

  const [selectedObjects, setSelectedObjects] = useState([]); // State for selected objects

  const [mode, setMode] = useState('select'); // Set initial mode to 'select'
  const {
    isSimulateActive,
    setIsSimulateActive,
    stitchColorMap,
    setStitchColorMap
  } = useSimulation();
  const [gridOffset, setGridOffset] = useState({ x: 100, y: 100 });
  const [isDrawingInGrid, setIsDrawingInGrid] = useState(false);
  const [hasSplinePoints, setHasSplinePoints] = useState(false);
  const [isThreadLoaded, setIsThreadLoaded] = useState(false);
  const [isVectorizeModalOpen, setIsVectorizeModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [isCrossParamsOpen, setIsCrossParamsOpen] = useState(false);
  const [isCrossConversionOpen, setIsCrossConversionOpen] = useState(false);
  const [crossViewMode, setCrossViewMode] = useState('stitch');
  const [crossTool, setCrossTool] = useState('select');
  const [selectedCrossColorId, setSelectedCrossColorId] = useState(0); // Default to 'not drawing'
  const [crossAreaParams, setCrossAreaParams] = useState(null);
  const [isCrossAreaSelecting, setIsCrossAreaSelecting] = useState(false);
  const [crossAreaSelection, setCrossAreaSelection] = useState(null);
  const [isCrossAreaImportSelecting, setIsCrossAreaImportSelecting] = useState(false);
  const [crossAreaImportSelection, setCrossAreaImportSelection] = useState(null);
  const [crossAreaSelectionSize, setCrossAreaSelectionSize] = useState(null);

  // Viewport and transformation logic extracted to useViewport hook
  const {
    dimensions,
    transform,
    setTransform,
    scrollbars,
    scrollbarDrag,
    screenToWorld,
    worldToGrid,
    handleWheel,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleFit,
    handleFitCanvas,
    handleScrollbarMouseDown,
    getScrollbarProps,
    getContext,
    clampTransform
  } = useViewport(canvasRef, gridAreaRef, containerRef, gridOffset);
  const {
    isMirrorMode,
    setIsMirrorMode,
    mirrorLinePoints,
    setMirrorLinePoints,
    mirrorPreviewPoint,
    setMirrorPreviewPoint,
    handleMirror,
    performMirrorOperation
  } = useMirroring(selectedObjects, setSelectedObjects, gridOffset, gridAreaRef, setMode);
  const {
    isSelectingArea,
    selectionArea,
    setSelectionArea,
    handleSaveProject,
    handleLoadProject,
    handleImportSVG,
    handleImportSVGText,
    performSvgPlacement
  } = useProjectIO(gridOffset, setGridOffset, gridAreaRef, setSelectedObjects, setMode, transform);

  useEffect(() => {
    GraphicPrimitive.loadThreadImage().then((img) => {
      if (img) setIsThreadLoaded(true);
    });
  }, []);

  // Toggle for rendering stitches as realistic textured thread or simple colored lines
  const [realityMode, setRealityMode] = useState(true);

  useEffect(() => {
    GraphicPrimitive.realityMode = realityMode;
    // Force a redraw so the rendering mode change takes effect immediately
    gridAreaRef.current?.redraw();
  }, [realityMode]);

  // Toggle to show entry point markers (start/end) as 0.5 mm circles
  const [showEntryPoint, setShowEntryPoint] = useState(false);

  useEffect(() => {
    // Force a redraw so the new marker visibility takes effect immediately
    gridAreaRef.current?.redraw();
  }, [showEntryPoint]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        const finished = gridAreaRef.current?.finishCurrentObject();
        if (finished) {
          setHasSplinePoints(false);
        }
      }
      if (event.key === 'Escape') {
        // Deselect all objects
        const objects = gridAreaRef.current?.getGraphicObjects() || [];
        objects.forEach(obj => {
          obj.isSelected = false;
        });
        setSelectedObjects([]);
        // Reset any dragging state
        setTransform(prev => ({
          ...prev,
          isDragging: false,
          isPanningCandidate: false,
          isMovingGrid: false,
        }));
        // Clear GridArea's internal dragging state
        gridAreaRef.current?.clearDraggingState();
        gridAreaRef.current?.redraw();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);


  const isPointInGrid = useCallback((worldX, worldY) => {
    return gridAreaRef.current?.isPointInBounds(worldX, worldY) || false;
  }, []);


  const handleMouseDown = (event) => {
    event.preventDefault();
    if (scrollbarDrag.isDragging) return;
    const { x: worldX, y: worldY } = screenToWorld(event.clientX, event.clientY);

    if (isSelectingArea) {
      setSelectionArea({ startX: worldX, startY: worldY, endX: worldX, endY: worldY });
      return;
    }

    if (isCrossAreaSelecting && event.button === 0) {
      setCrossAreaSelection({ startX: worldX, startY: worldY, endX: worldX, endY: worldY });
      return;
    }

    if (isCrossAreaImportSelecting && event.button === 0) {
      setCrossAreaSelection({ startX: worldX, startY: worldY, endX: worldX, endY: worldY });
      return;
    }

    // Handle mirror mode - collect points for mirror line
    if (isMirrorMode && mode === 'mirror') {
      if (event.button === 0) { // Left click
        if (mirrorLinePoints.length < 2) {
          const newPoints = [...mirrorLinePoints, { x: worldX, y: worldY }];
          setMirrorLinePoints(newPoints);

          if (newPoints.length === 2) {
            // Perform mirror operation
            performMirrorOperation(newPoints[0], newPoints[1]);
            setIsMirrorMode(false);
            setMode('select');
            setMirrorLinePoints([]);
            setMirrorPreviewPoint(null);
          }
        }
        return;
      }
      if (event.button === 2) { // Right click to cancel
        setIsMirrorMode(false);
        setMode('select');
        setMirrorLinePoints([]);
        setMirrorPreviewPoint(null);
        return;
      }
    }

    if (event.button === 2) {
      if (mode === 'polyspline') {
        // Fall through to handles below
      } else if ((mode === 'spline' || mode === 'poly' || mode === 'curve') && hasSplinePoints) {
        return;
      } else if (isPointInGrid(worldX, worldY)) {
        setTransform(prev => ({
          ...prev,
          isMovingGrid: true,
          dragStart: { x: event.clientX, y: event.clientY },
        }));
        return;
      } else {
        setTransform(prev => ({
          ...prev,
          isPanningCandidate: true,
          dragStart: { x: event.clientX, y: event.clientY },
        }));
        return;
      }
    }

    // Handle selection logic in 'select' mode
    if (mode === 'select') {
      // If clicking outside the grid in select mode, deselect all objects
      if (!isPointInGrid(worldX, worldY)) {
        const objects = gridAreaRef.current?.getGraphicObjects() || [];
        let selectionChanged = false;
        objects.forEach(obj => {
          if (obj.isSelected) {
            obj.isSelected = false;
            selectionChanged = true;
          }
        });
        if (selectionChanged) {
          setSelectedObjects([]);
          gridAreaRef.current?.redraw();
        }
        return;
      }
      // In select mode with cross tools active, let cross tool interactions happen
      // Otherwise, continue to GridArea's handleMouseDown for selection/dragging
      if (currentTab === 5 && crossTool !== 'select') {
        // Let cross tool interaction happen below
      } else {
        // Continue to GridArea's handleMouseDown for selection/dragging
      }
    }

    if (isPointInGrid(worldX, worldY)) {
      if (currentTab === 5 && crossTool !== 'select') {
        const gridPos = worldToGrid(worldX, worldY);
        let colorToPaint = null;
        if (crossTool === 'paint') {
          colorToPaint = selectedCrossColorId;
        } else if (crossTool === 'erase') {
          colorToPaint = 0; // The 'not-drawing' color ID
        }

        const pickedId = gridAreaRef.current?.handleCrossInteraction(
          gridPos.x,
          gridPos.y,
          crossTool,
          colorToPaint
        );

        if (crossTool === 'pick' && pickedId !== -1) {
          setSelectedCrossColorId(pickedId);
        }
      } else {
        setIsDrawingInGrid(true);
        gridAreaRef.current?.handleMouseDown(worldX, worldY, event);
      }
    }
  };

  const handleMouseUp = (event) => {
    event.preventDefault();
    if (scrollbarDrag.isDragging) return;
    const { x: worldX, y: worldY } = screenToWorld(event.clientX, event.clientY);

    if (isSelectingArea) {
      performSvgPlacement();
      return;
    }

    if (isCrossAreaSelecting && crossAreaSelection && event.button === 0) {
      // Calculate dimensions from selection area
      const width = Math.abs(crossAreaSelection.endX - crossAreaSelection.startX);
      const height = Math.abs(crossAreaSelection.endY - crossAreaSelection.startY);

      // Calculate approximate cols/rows based on default cross size (10)
      const defaultCrossSize = 10;
      const cols = Math.max(1, Math.round(width / defaultCrossSize));
      const rows = Math.max(1, Math.round(height / defaultCrossSize));
      const crossSize = width / cols; // Adjust cross size to fit selection

      setCrossAreaParams({ cols, rows, crossSize });
      setCrossAreaSelectionSize({ width, height });
      setIsCrossAreaSelecting(false);
      setIsCrossParamsOpen(true);
      return;
    }

    if (isCrossAreaImportSelecting && crossAreaSelection && event.button === 0) {
      // Calculate dimensions from selection area for import
      const width = Math.abs(crossAreaSelection.endX - crossAreaSelection.startX);
      const height = Math.abs(crossAreaSelection.endY - crossAreaSelection.startY);

      // Calculate approximate cols/rows based on default cross size (10)
      const defaultCrossSize = 10;
      const cols = Math.max(1, Math.round(width / defaultCrossSize));
      const rows = Math.max(1, Math.round(height / defaultCrossSize));
      const crossSize = width / cols; // Adjust cross size to fit selection

      setCrossAreaParams({ cols, rows, crossSize });
      setCrossAreaImportSelection(crossAreaSelection); // Store selection for later use
      setIsCrossAreaImportSelecting(false);
      setIsCrossConversionOpen(true);
      return;
    }

    if (event.button === 2 && mode === 'polyspline') {
      gridAreaRef.current?.handleMouseUp(worldX, worldY, event);
      return;
    }

    if (event.button === 2 && (mode === 'spline' || mode === 'poly' || mode === 'curve')) {
      gridAreaRef.current?.handleMouseUp(worldX, worldY, event);
      setTransform(prev => ({ ...prev, isMovingGrid: false }));
      return;
    }
    if (transform.isMovingGrid) {
      setTransform(prev => ({ ...prev, isMovingGrid: false }));
      return;
    }
    if (transform.isPanningCandidate) {
      const wasDragging = transform.isDragging;
      setTransform(prev => ({ ...prev, isDragging: false, isPanningCandidate: false }));
      if (wasDragging) {
        return;
      }
    }
    if (isDrawingInGrid) {
      gridAreaRef.current?.handleMouseUp(worldX, worldY, event);
      setIsDrawingInGrid(false);
    }
  };

  const handleMouseMove = (event) => {
    const ctx = getContext();
    if (!ctx) return;
    if (scrollbarDrag.isDragging) return;
    const { x: worldX, y: worldY } = screenToWorld(event.clientX, event.clientY);
    const dragThreshold = 5;

    if (isSelectingArea && selectionArea) {
      setSelectionArea(prev => ({ ...prev, endX: worldX, endY: worldY }));
      return;
    }

    if (isCrossAreaSelecting && crossAreaSelection) {
      setCrossAreaSelection(prev => ({ ...prev, endX: worldX, endY: worldY }));
      return;
    }

    if (isCrossAreaImportSelecting && crossAreaSelection) {
      setCrossAreaSelection(prev => ({ ...prev, endX: worldX, endY: worldY }));
      return;
    }

    // Handle mirror mode - update preview line
    if (isMirrorMode && mode === 'mirror' && mirrorLinePoints.length === 1) {
      setMirrorPreviewPoint({ x: worldX, y: worldY });
      return;
    }

    if (transform.isMovingGrid) {
      const dx = event.clientX - transform.dragStart.x;
      const dy = event.clientY - transform.dragStart.y;
      const scaledDx = dx / transform.scale;
      const scaledDy = dy / transform.scale;
      setGridOffset(prev => ({ x: prev.x + scaledDx, y: prev.y + scaledDy }));
      setTransform(prev => ({ ...prev, dragStart: { x: event.clientX, y: event.clientY } }));
      return;
    }

    if (transform.isPanningCandidate) {
      const dx = event.clientX - transform.dragStart.x;
      const dy = event.clientY - transform.dragStart.y;

      if (transform.isDragging) {
        const newTranslateX = transform.translateX + dx;
        const newTranslateY = transform.translateY + dy;

        const { translateX: clampedX, translateY: clampedY } = clampTransform({
          ...transform,
          translateX: newTranslateX,
          translateY: newTranslateY,
        });

        setTransform(prev => ({
          ...prev,
          translateX: clampedX,
          translateY: clampedY,
          dragStart: { x: event.clientX, y: event.clientY },
        }));
        return;

      } else if (Math.sqrt(dx * dx + dy * dy) > dragThreshold) {
        setTransform(prev => ({ ...prev, isDragging: true, }));
      }
      return;
    }

    if (isDrawingInGrid || isPointInGrid(worldX, worldY)) {
      if (currentTab === 5 && crossTool !== 'select') {
        // Do nothing on mouse move for cross tools, they should only act on click (handleMouseDown)
        // Or if pick color is meant to work on hover, it should be explicitly handled here.
        // For now, consistent with user's request, remove move interaction for all cross tools.
        // If there's a need for visual feedback on hover (e.g., cell highlight), it should be
        // implemented in GridArea's handleMouseMove, not via handleCrossInteraction.
      } else {
        gridAreaRef.current?.handleMouseMove(worldX, worldY, event);
      }
    }
  };




  const handleSelectionChange = useCallback((objects) => {
    // GridArea now returns an array
    setSelectedObjects(objects || []);
  }, []);

  const handlePropertyChange = useCallback((property, value) => {
    // Apply to all selected objects
    if (selectedObjects.length > 0) {
      selectedObjects.forEach(obj => {
        // Map 'type' to 'fillType' for the object property
        const propName = property === 'type' ? 'fillType' : property;
        obj[propName] = value;
      });

      // Force update/redraw
      gridAreaRef.current?.redraw();
      // Force update of local state
      setSelectedObjects([...selectedObjects]);
    }
  }, [selectedObjects]);

  const handleCrossAreaClick = useCallback(() => {
    // Create ribbon: just start area selection for simple cross area creation
    setIsCrossAreaSelecting(true);
  }, []);

  const handleCrossImportClick = useCallback(() => {
    // Crosses ribbon Import: first select area, then open conversion dialog
    setIsCrossAreaImportSelecting(true);
  }, []);

  const handleCrossAreaConfirm = useCallback((params) => {
    // Create CrossArea at the selected position (convert world to grid coordinates)
    const worldStartX = Math.min(crossAreaSelection.startX, crossAreaSelection.endX);
    const worldStartY = Math.min(crossAreaSelection.startY, crossAreaSelection.endY);
    const gridStart = worldToGrid(worldStartX, worldStartY);

    const newObj = new CrossArea(
      gridStart.x,
      gridStart.y,
      params.cols,
      params.rows,
      params.crossSize,
      color,
      fillColor
    );
    newObj.isSelected = true;
    gridAreaRef.current?.addGraphicObject(newObj);
    setMode('select');
    setIsCrossAreaSelecting(false);
    setCrossAreaSelection(null);
    setCrossAreaParams(null);
    setSelectedObjects([newObj]);
    setCurrentTab(5); // Switch to Crosses tab
  }, [color, fillColor, crossAreaSelection, worldToGrid]);

  const handleCrossImport = useCallback((data) => {
    // Import has full cell data, place at selected position.
    // cols/rows/crossSize are already calculated to fit the selection.
    let startX, startY;
    
    if (crossAreaImportSelection) {
      const worldStartX = Math.min(crossAreaImportSelection.startX, crossAreaImportSelection.endX);
      const worldStartY = Math.min(crossAreaImportSelection.startY, crossAreaImportSelection.endY);
      const gridStart = worldToGrid(worldStartX, worldStartY);
      startX = gridStart.x;
      startY = gridStart.y;
    } else {
      startX = gridOffset.x;
      startY = gridOffset.y;
    }

    const newObj = new CrossArea(
      startX,
      startY,
      data.cols,
      data.rows,
      data.crossSize,
      color,
      fillColor,
      data.cells,
      data.colorPalette
    );
    newObj.isSelected = true;
    gridAreaRef.current?.addGraphicObject(newObj);
    setMode('select');
    setSelectedObjects([newObj]);
    setCurrentTab(5); // Switch to Crosses tab
    setCrossAreaImportSelection(null); // Clear stored selection
  }, [color, fillColor, gridOffset, crossAreaImportSelection, worldToGrid]);

  useEffect(() => {
    if (selectedObjects.length === 1 && selectedObjects[0] instanceof CrossArea) {
      selectedObjects[0].viewMode = crossViewMode;
      gridAreaRef.current?.redraw();
    }
  }, [crossViewMode, selectedObjects]);


  return (
    <div className="work-area" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <Ribbon
        color={color}
        setColor={setColor}
        fillColor={fillColor}
        setFillColor={setFillColor}
        fillProps={fillProps}
        setFillProps={setFillProps}
        mode={mode}
        onModeChange={setMode}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleResetZoom}
        onFit={handleFit}
        onFitCanvas={handleFitCanvas}
        onSaveProject={handleSaveProject}
        onLoadProject={handleLoadProject}
        onImportSVG={handleImportSVG}
        onVectorize={() => setIsVectorizeModalOpen(true)}
        realityMode={realityMode}
        onRealityToggle={setRealityMode}
        showEntryPoint={showEntryPoint}
        onShowEntryToggle={setShowEntryPoint}
        currentStitchIndex={currentStitchIndex}
        setCurrentStitchIndex={setCurrentStitchIndex}
        totalStitches={totalStitches}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        onSimulateToggle={setIsSimulateActive}
        stitchColorMap={stitchColorMap}
        simulationSpeed={simulationSpeed}
        setSimulationSpeed={setSimulationSpeed}
        onMirror={handleMirror}
        isMirrorMode={isMirrorMode}
        setIsMirrorMode={setIsMirrorMode}
        onCrossAreaClick={handleCrossAreaClick}
        crossViewMode={crossViewMode}
        setCrossViewMode={setCrossViewMode}
        crossTool={crossTool}
        setCrossTool={setCrossTool}
        onImportCrosses={handleCrossImportClick}
        currentTab={currentTab}
        onTabChange={setCurrentTab}
      />
      <div className="main-content" style={{ display: 'flex', flexDirection: 'row', flex: 1, overflow: 'hidden' }}>
        <div
          ref={containerRef}
          className="canvas-container"
          style={{
            position: 'relative',
            flex: 1,
            overflow: 'hidden',
            backgroundColor: '#ccc' // Just to see the background if canvas doesn't cover
          }}
        >
          <canvas
            ref={canvasRef}
            width={dimensions.width}
            height={dimensions.height}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onWheel={handleWheel}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              border: '1px solid black',
              // Update cursor based on the current mode
              cursor: mode === 'select' ? 'default' :
                mode === 'mirror' ? 'crosshair' :
                  scrollbarDrag.isDragging ? 'grabbing' :
                    transform.isDragging ? 'grabbing' :
                      transform.isMovingGrid ? 'move' :
                        (isCrossAreaSelecting || isCrossAreaImportSelecting) ? 'crosshair' : 'crosshair',
              position: 'relative',
              zIndex: 0
            }}
          />
          {isSelectingArea && selectionArea && (
            <div
              style={{
                position: 'absolute',
                border: '2px dashed #000',
                backgroundColor: 'rgba(60, 120, 220, 0.12)',
                pointerEvents: 'none',
                zIndex: 999,

                // ────────────────────────────────────────────────
                //   Correct screen-space calculation
                // ────────────────────────────────────────────────
                left: Math.min(selectionArea.startX, selectionArea.endX) * transform.scale + transform.translateX,
                top: Math.min(selectionArea.startY, selectionArea.endY) * transform.scale + transform.translateY,
                width: Math.abs(selectionArea.endX - selectionArea.startX) * transform.scale,
                height: Math.abs(selectionArea.endY - selectionArea.startY) * transform.scale,

                boxSizing: 'border-box',
              }}
            />
          )}
          {(isCrossAreaSelecting || isCrossAreaImportSelecting) && crossAreaSelection && (
            <div
              style={{
                position: 'absolute',
                border: '2px dashed #ff6600',
                backgroundColor: 'rgba(255, 102, 0, 0.12)',
                pointerEvents: 'none',
                zIndex: 999,
                left: Math.min(crossAreaSelection.startX, crossAreaSelection.endX) * transform.scale + transform.translateX,
                top: Math.min(crossAreaSelection.startY, crossAreaSelection.endY) * transform.scale + transform.translateY,
                width: Math.abs(crossAreaSelection.endX - crossAreaSelection.startX) * transform.scale,
                height: Math.abs(crossAreaSelection.endY - crossAreaSelection.startY) * transform.scale,
                boxSizing: 'border-box',
              }}
            />
          )}
          {/* Mirror line visualization */}
          {isMirrorMode && mirrorLinePoints.length >= 1 && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 999,
              }}
            >
              <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
                <line
                  x1={mirrorLinePoints[0].x * transform.scale + transform.translateX}
                  y1={mirrorLinePoints[0].y * transform.scale + transform.translateY}
                  x2={(mirrorPreviewPoint?.x || mirrorLinePoints[0].x) * transform.scale + transform.translateX}
                  y2={(mirrorPreviewPoint?.y || mirrorLinePoints[0].y) * transform.scale + transform.translateY}
                  stroke="#ff0000"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
                <circle
                  cx={mirrorLinePoints[0].x * transform.scale + transform.translateX}
                  cy={mirrorLinePoints[0].y * transform.scale + transform.translateY}
                  r="5"
                  fill="#ff0000"
                />
                {mirrorPreviewPoint && (
                  <circle
                    cx={mirrorPreviewPoint.x * transform.scale + transform.translateX}
                    cy={mirrorPreviewPoint.y * transform.scale + transform.translateY}
                    r="5"
                    fill="#ff0000"
                  />
                )}
                <text
                  x={50}
                  y={30}
                  fill="#ff0000"
                  fontSize="14"
                  fontWeight="bold"
                  style={{ textShadow: '1px 1px 2px white' }}
                >
                  {mirrorLinePoints.length === 1 ? "Click to set second point of mirror line (Right-click to cancel)" : ""}
                </text>
              </svg>
            </div>
          )}
          <GridArea
            ref={gridAreaRef}
            gridWidth={600}
            gridHeight={400}
            canvasWidth={dimensions.width}
            canvasHeight={dimensions.height}
            spacing={5}
            transform={transform}
            mode={mode}
            color={color}
            fillColor={fillColor}
            fillProps={fillProps}
            gridOffset={gridOffset}
            onGridOffsetChange={setGridOffset}
            onSplinePointsChange={setHasSplinePoints}
            onSelectionChange={handleSelectionChange}
            isThreadLoaded={isThreadLoaded}
            showEntryPoint={showEntryPoint}
            currentStitchIndex={isSimulateActive ? currentStitchIndex : Infinity}
            setTotalStitches={setTotalStitches}
            setStitchColorMap={setStitchColorMap}
            crossAreaParams={crossAreaParams}
          />
          {scrollbars.horizontal && (
            <div
              className="scrollbar horizontal-scrollbar"
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '16px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderTop: 'none',
                zIndex: 10
              }}
            >
              <div
                className="scrollbar-thumb"
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: `${getScrollbarProps().horizontal.thumbLeft}px`,
                  width: `${getScrollbarProps().horizontal.thumbWidth}px`,
                  height: '12px',
                  backgroundColor: '#888',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
                onMouseDown={(e) => handleScrollbarMouseDown(e, 'horizontal')}
              />
            </div>
          )}
          {scrollbars.vertical && (
            <div
              className="scrollbar vertical-scrollbar"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '16px',
                height: '100%',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderLeft: 'none',
                zIndex: 10
              }}
            >
              <div
                className="scrollbar-thumb"
                style={{
                  position: 'absolute',
                  left: '2px',
                  top: `${getScrollbarProps().vertical.thumbTop}px`,
                  width: '12px',
                  height: `${getScrollbarProps().vertical.thumbHeight}px`,
                  backgroundColor: '#888',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
                onMouseDown={(e) => handleScrollbarMouseDown(e, 'vertical')}
              />
            </div>
          )}
          {scrollbars.horizontal && scrollbars.vertical && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '16px',
                height: '16px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderLeft: 'none',
                borderTop: 'none',
                zIndex: 10
              }}
            />
          )}

        </div>
        <div className="right-panel-container" style={{ width: '250px', borderLeft: '1px solid #ccc', overflowY: 'auto' }}>
          <RightPanel
            selectedObject={selectedObjects.length === 1 ? selectedObjects[0] : null}
            onPropertyChange={handlePropertyChange}
            selectedCrossColorId={selectedCrossColorId}
            onSelectedCrossColorIdChange={setSelectedCrossColorId}
          />
        </div>
      </div>
      <StatusBar mode={mode} isCrossAreaSelecting={isCrossAreaSelecting} isMirrorMode={isMirrorMode} />
      <VectorizeModal
        open={isVectorizeModalOpen}
        onClose={() => setIsVectorizeModalOpen(false)}
        onImport={handleImportSVGText}
      />
      <CrossAreaParamsDialog
        open={isCrossParamsOpen}
        onClose={() => {
          setIsCrossParamsOpen(false);
          setIsCrossAreaSelecting(false);
          setCrossAreaSelection(null);
          setCrossAreaParams(null);
          setCrossAreaSelectionSize(null);
        }}
        onConfirm={handleCrossAreaConfirm}
        initialValues={crossAreaParams}
        selectionSize={crossAreaSelectionSize}
      />
      <CrossConversionModal
        open={isCrossConversionOpen}
        onClose={() => {
          setIsCrossConversionOpen(false);
          setCrossAreaImportSelection(null);
        }}
        onImport={handleCrossImport}
        selectionSize={crossAreaImportSelection ? {
          width: Math.abs(crossAreaImportSelection.endX - crossAreaImportSelection.startX),
          height: Math.abs(crossAreaImportSelection.endY - crossAreaImportSelection.startY)
        } : null}
      />
    </div>
  );
}

export default WorkArea;